import React from 'react';
import { localizePath, getNavigationData } from '@/lib/config/utils';
import type { NavigationItem, FooterSection } from '@/lib/config';
import ProductBreadcrumb from './catalog/ProductBreadcrumb';
import { getBranchCodeFromPath, regionalizePath } from '@/lib/config/branches-config';

interface StaticBreadcrumbWrapperProps {
  pathname: string;
  langCode: string;
  pageTitle?: string;
  /** Филиал страницы; на хосте филиала (BRANCH_ROUTING=host) путь его не содержит */
  branchCode?: string;
}

// Маппинг путей к названиям страниц (можно расширять)
const pageNameMap: Record<string, string> = {
  'favorites': 'Сохранённое',
  'company': 'О компании',
  'about': 'О компании',
  'contacts': 'Контакты',
  'shops': 'Магазины',
  'customer': 'Покупателям',
  'delivery': 'Доставка и оплата',
  'faq': 'Вопросы и ответы',
  'legal': 'Правовая информация',
  'politika-konfidentsialnosti': 'Политика конфиденциальности',
  'user-agreement': 'Согласие на обработку',
  'oferta': 'Публичная оферта',
  'cookies': 'Политика Cookies',
};

// Функция для получения названия страницы из навигации
function getPageNameFromNavigation(langCode: string, path: string, segment: string): string | null {
  // Проверяем навигацию компании
  if (segment === 'company' || path.includes('/company/')) {
    const companyNav = getNavigationData(langCode, 'company') as NavigationItem[];
    const navItem = companyNav.find(item => {
      const itemPath = item.url.startsWith('/') ? item.url : `/${item.url}`;
      return itemPath === path || itemPath === `/${segment}` || itemPath === path.replace(/^\/[a-z]{2}/, '');
    });
    if (navItem) return navItem.name;
  }

  // Проверяем footer навигацию
  const footerNav = getNavigationData(langCode, 'footer') as FooterSection[];
  for (const section of footerNav) {
    const navItem = section.links.find(item => {
      const itemPath = item.url.startsWith('/') ? item.url : `/${item.url}`;
      return itemPath === path || itemPath === `/${segment}` || itemPath === path.replace(/^\/[a-z]{2}/, '');
    });
    if (navItem) return navItem.name;
  }

  return null;
}

export default function StaticBreadcrumbWrapper({
  pathname,
  langCode,
  pageTitle,
  branchCode: branchCodeProp,
}: StaticBreadcrumbWrapperProps) {
  // Филиал передают обёртки (по хосту/x-branch); без него — из пути, как раньше.
  const branchCode = branchCodeProp || getBranchCodeFromPath(pathname);
  // Убираем языковой префикс из pathname для обработки
  const normalizedPath = regionalizePath(pathname.replace(/^\/[a-z]{2}\//, '/'), 'spb');
  const breadcrumbPath = (path: string) => regionalizePath(localizePath(path, langCode), branchCode);
  
  // Разбиваем путь на сегменты
  const pathSegments = normalizedPath.split('/').filter(segment => segment && segment !== '');
  
  // Если путь слишком короткий (только корень), не показываем breadcrumb
  if (pathSegments.length === 0) {
    return null;
  }

  const breadcrumbItems: Array<{ slug: string; name: string; href: string }> = [];

  // Добавляем "Главная" как первый элемент
  breadcrumbItems.push({
    slug: 'home',
    name: 'Главная',
    href: breadcrumbPath('/'),
  });

  // Сначала получаем название текущей страницы (последний элемент)
  const lastSegment = pathSegments[pathSegments.length - 1];
  const lastPath = pathSegments.reduce((acc, seg) => acc + `/${seg}`, '');
  const lastNavName = getPageNameFromNavigation(langCode, lastPath, lastSegment);
  const currentPageName = pageTitle || lastNavName || pageNameMap[lastSegment] || lastSegment;

  // Специальная обработка для страниц контактов в СПб и региональных разделах
  // Показываем только "Главная" и "Контакты", пропуская промежуточные сегменты (geo, msk, company)
  const isContactsPage = lastSegment === 'contacts' && normalizedPath.includes('/company/contacts');
  
  if (isContactsPage) {
    // Для страниц контактов показываем только "Главная" и "Контакты"
    return <ProductBreadcrumb items={breadcrumbItems} productName={currentPageName} />;
  }

  // Строим breadcrumb для каждого сегмента пути (кроме последнего)
  let currentPath = '';
  for (let i = 0; i < pathSegments.length - 1; i++) {
    const segment = pathSegments[i];
    currentPath += `/${segment}`;

    // Получаем название для сегмента
    let segmentName = pageNameMap[segment] || segment;
    
    // Пытаемся получить название из навигации
    const navName = getPageNameFromNavigation(langCode, currentPath, segment);
    if (navName) {
      segmentName = navName;
    }

    // Не добавляем промежуточный сегмент, если его название совпадает с названием текущей страницы
    if (segmentName !== currentPageName) {
      breadcrumbItems.push({
        slug: segment,
        name: segmentName,
        href: breadcrumbPath(currentPath === '/company' ? '/company/about/' : currentPath),
      });
    }
  }

  // Если нет элементов кроме "Главная", не показываем breadcrumb
  if (breadcrumbItems.length <= 1 && !pageTitle) {
    return null;
  }

  return <ProductBreadcrumb items={breadcrumbItems} productName={currentPageName} />;
}

